/**
 * Canonical date / date-time display helpers — the single source of
 * truth for how absolute timestamps render across the dashboard.
 *
 * Shape per the copy-audit Conventions tab: month spelled, 12-hour
 * clock, no seconds, middle-dot separator — e.g.
 * "Jun 13, 2026 · 3:14 PM". Field selection is fixed so the shape is
 * stable across every surface; the locale stays the user's default so
 * month names and AM/PM follow their environment.
 *
 * Replaces scattered ad-hoc `new Date(x).toLocaleString()` calls,
 * which produced inconsistent formats (24-hour, seconds, slash dates)
 * across Reports, Tracker, Kiosks, Users, and Bay access.
 */
const DATE_OPTS: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' }
const TIME_OPTS: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit' }

function toDate(value: string | number | Date | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') return null
  const d = value instanceof Date ? value : new Date(value)
  return Number.isNaN(d.getTime()) ? null : d
}

/** Absolute date + time, e.g. "Jun 13, 2026 · 3:14 PM". Em-dash for empty. */
export function formatDateTime(value: string | number | Date | null | undefined): string {
  const d = toDate(value)
  if (d === null) return '—'
  return `${d.toLocaleDateString(undefined, DATE_OPTS)} · ${d.toLocaleTimeString(undefined, TIME_OPTS)}`
}

/** Date only, e.g. "Jun 13, 2026". Em-dash for empty. */
export function formatDate(value: string | number | Date | null | undefined): string {
  const d = toDate(value)
  if (d === null) return '—'
  return d.toLocaleDateString(undefined, DATE_OPTS)
}
